import React, { useState } from 'react';
import { HelpCircle, Bot, MessageCircle, Book, PlayCircle, Lightbulb, Search } from 'lucide-react';
import { AppAiAssistantPanel } from '../ai/AppAiAssistantPanel';
import { HelpDocViewer } from './HelpDocViewer';
import { HelpTourOverlay, mockTours, Tour } from './HelpTourOverlay';
import { useHelpContext } from './HelpContextProvider';

export const AppHelpTrigger: React.FC = () => {
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [activeTour, setActiveTour] = useState<Tour | null>(null);
  const [showTourMenu, setShowTourMenu] = useState(false);
  
  const { currentContext, getContextualHelp } = useHelpContext();
  
  // Get available tours for current context
  const availableTours = mockTours.filter(tour => {
    const pageMatch = tour.context.pages.some(page => 
      currentContext.page === page || currentContext.page.startsWith(page)
    );
    const moduleMatch = tour.context.modules.includes(currentContext.module);
    const roleMatch = tour.context.roles.some(role => 
      currentContext.userRole.includes(role)
    );
    return pageMatch && moduleMatch && roleMatch;
  });
  
  const contextualHelp = getContextualHelp();
  const hasContextualContent = contextualHelp.length > 0 || availableTours.length > 0;

  const helpOptions = [
    {
      icon: Bot,
      label: 'Assistant IA',
      description: 'Aide intelligente et suggestions',
      badge: 'IA',
      onClick: () => {
        setShowAiAssistant(true);
        setShowHelpMenu(false);
      }
    },
    {
      icon: Book,
      label: 'Documentation',
      description: `${contextualHelp.length} article(s) pour cette page`,
      badge: hasContextualContent ? contextualHelp.length.toString() : undefined,
      onClick: () => {
        setShowDocViewer(true);
        setShowHelpMenu(false);
      }
    },
    {
      icon: PlayCircle,
      label: 'Visite guidée',
      description: `${availableTours.length} tour(s) disponible(s)`,
      badge: availableTours.length > 0 ? availableTours.length.toString() : undefined,
      disabled: availableTours.length === 0,
      onClick: () => {
        if (availableTours.length === 1) {
          setActiveTour(availableTours[0]);
          setShowHelpMenu(false);
        } else {
          setShowTourMenu(true);
        }
      }
    },
    {
      icon: Search,
      label: 'Recherche rapide',
      description: 'Trouvez rapidement une réponse',
      onClick: () => {
        setShowDocViewer(true);
        setShowHelpMenu(false);
        // Focus search input after opening
        setTimeout(() => {
          const searchInput = document.querySelector('[placeholder*="Rechercher"]') as HTMLInputElement;
          if (searchInput) searchInput.focus();
        }, 100);
      }
    },
    {
      icon: MessageCircle,
      label: 'Support technique',
      description: 'Contactez notre équipe',
      onClick: () => {
        // Open support chat or form
        window.open('mailto:support@plvplus.com?subject=Support PLV+ - ' + currentContext.page, '_blank');
        setShowHelpMenu(false);
      }
    }
  ];
  
  const startTour = (tour: Tour) => {
    setActiveTour(tour);
    setShowTourMenu(false);
    setShowHelpMenu(false);
  };
  
  const completeTour = (tourId: string) => {
    // Save completed tour to localStorage or API
    const completedTours = JSON.parse(localStorage.getItem('completedTours') || '[]');
    if (!completedTours.includes(tourId)) {
      completedTours.push(tourId);
      localStorage.setItem('completedTours', JSON.stringify(completedTours));
    }
    setActiveTour(null);
  };

  return (
    <>
      {/* Floating help button */}
      <div className="fixed bottom-6 right-6 z-40">
        <div className="relative">
          {/* Help menu */}
          {showHelpMenu && (
            <div className="absolute bottom-16 right-0 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2">
              {/* Context indicator */}
              {hasContextualContent && (
                <div className="px-3 py-2 mb-2 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                  <div className="flex items-center text-sm text-blue-800 dark:text-blue-300">
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Aide contextuelle disponible
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {currentContext.module} • {currentContext.page}
                  </div>
                </div>
              )}
              
              <div className="space-y-1">
                {helpOptions.map((option) => (
                  <button
                    key={option.label}
                    onClick={option.onClick}
                    disabled={option.disabled}
                    className={`w-full flex items-start space-x-3 p-3 rounded-md text-left transition-colors ${
                      option.disabled 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <option.icon className="h-5 w-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {option.label}
                        </p>
                        {option.badge && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
                            {option.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {option.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Tour selection menu */}
          {showTourMenu && (
            <div className="absolute bottom-16 right-0 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  Visites guidées disponibles
                </h3>
                <button
                  onClick={() => setShowTourMenu(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                {availableTours.map((tour) => (
                  <button
                    key={tour.id}
                    onClick={() => startTour(tour)}
                    className="w-full text-left p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {tour.title}
                      </h4>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{tour.estimatedDuration} min</span>
                        <span className={`px-2 py-0.5 rounded ${
                          tour.difficulty === 'beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                          tour.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300' :
                          'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                        }`}>
                          {tour.difficulty}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {tour.description}
                    </p>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {tour.steps.length} étapes
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Main help button */}
          <button
            onClick={() => setShowHelpMenu(!showHelpMenu)}
            className={`w-14 h-14 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group relative ${
              hasContextualContent ? 'ring-2 ring-blue-300 ring-opacity-50' : ''
            }`}
            data-tour="help-trigger"
          >
            <HelpCircle className="h-6 w-6 group-hover:scale-110 transition-transform" />
            
            {/* Context indicator */}
            {hasContextualContent && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {contextualHelp.length + availableTours.length}
                </span>
              </div>
            )}
          </button>

          {/* Pulse animation for attention */}
          <div className={`absolute inset-0 rounded-full bg-primary-600 transition-opacity duration-300 ${
            hasContextualContent ? 'animate-ping opacity-20' : 'opacity-0'
          }`}></div>
        </div>
      </div>

      {/* AI Assistant Panel */}
      <AppAiAssistantPanel 
        isOpen={showAiAssistant} 
        onClose={() => setShowAiAssistant(false)} 
      />
      
      {/* Documentation Viewer */}
      <HelpDocViewer 
        isOpen={showDocViewer}
        onClose={() => setShowDocViewer(false)}
      />
      
      {/* Tour Overlay */}
      <HelpTourOverlay
        tour={activeTour}
        isActive={!!activeTour}
        onClose={() => setActiveTour(null)}
        onComplete={completeTour}
      />

      {/* Close menus when clicking outside */}
      {(showHelpMenu || showTourMenu) && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => {
            setShowHelpMenu(false);
            setShowTourMenu(false);
          }}
        />
      )}
    </>
  );
};
